import { Location } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef,ElementRef  } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { applicantHomeRoutesNames } from 'src/app/services/mock-data/applicant-home-constants/applicant-home-constants';
import { User } from 'src/app/models';
import { UserAddress } from 'src/app/models/address';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DocumentService,
  SecurityService,
  WorkflowContextService
} from 'src/app/services';

@Component({
  selector: 'wm-profile-form',
  standalone: false,
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss']
})
export class ProfileFormComponent implements OnInit {
  userId: string;
  private userRecord: Partial<User> | null = null;
  isSaving = false;
  form!: FormGroup;

  constructor(
    public context: WorkflowContextService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private _securitySvc: SecurityService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) {
    this.userId = this.context.user.id;
  }

  // -----------------------------
  // DYNAMIC CHECKBOX LIST
  // -----------------------------
  otherDetailsOptions = [
    { label: 'I am a Homeowner, Tenant, or Unlicensed Agent', checked: false },
    { label: 'I am a Contractor', checked: false },
    { label: 'Septic Designer', checked: false },
    { label: 'I am an Establishment, Restaurant or Facility', checked: false },
    { label: 'I am a Real Estate Agent', checked: false },
    { label: 'I am a Septic Inspector', checked: false }
  ];

  // -----------------------------
  // INPUT FIELD CHANGE HANDLERS
  // -----------------------------
  // Setting the value explicitly bridges the custom UI component back to our Angular Form validation logic.
  onFullNameChange(val: string) {
    this.form.get('fullName')?.setValue(val);
    this.form.get('fullName')?.markAsUntouched();
  }

  // Start Code added by Sanchit on 09/06/2026
  onUserNameChange(val: string) {
    this.form.get('userName')?.setValue(val);
    this.form.get('userName')?.markAsUntouched();
  }
  // End Code added by Sanchit on 09/06/2026

  onStreetAddressChange(val: string) {
    this.form.get('streetAddress')?.setValue(val);
    this.form.get('streetAddress')?.markAsUntouched();
  }
  onCityChange(val: string) {
    this.form.get('city')?.setValue(val);
    this.form.get('city')?.markAsUntouched();
  }
  onStateChange(val: string) {
    this.form.get('state')?.setValue(val);
    this.form.get('state')?.markAsUntouched();
  }
  onZipChange(val: string) {
    this.form.get('zip')?.setValue(val);
    this.form.get('zip')?.markAsUntouched();
  }
  onPhoneChange(val: string) {
    this.form.get('phone')?.setValue(val);
    this.form.get('phone')?.markAsUntouched();
  }
  onAltPhoneChange(val: string) {
    this.form.get('phone2')?.setValue(val);
    this.form.get('phone2')?.markAsUntouched();
  }
  onEmailChange(val: string) {
    this.form.get('email')?.setValue(val);
    this.form.get('email')?.markAsUntouched();
  }

  // -----------------------------
  // CHECKBOX CHANGE HANDLER
  // -----------------------------
  handleOtherDetailChange(index: number, checked: boolean) {
    this.otherDetailsOptions[index].checked = checked;
  }
  
  ngOnInit(): void {
    this.form = this.fb.group({
      fullName: ['', Validators.required],
      userName: ['', Validators.required], // Code added by Sanchit on 09/06/2026
      streetAddress: ['', Validators.required],
      city: [''],
      state: [''],
      zip: [''],
      phone: [''],
      phone2: [''],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/) 
        ]
      ]
    });

    this.form.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });

    const userId = this.userId; // replace with actual logged-in user id

    this._securitySvc.getUser(userId).subscribe({
      next: (user: User) => {
        this.userRecord = user;
        const address = user.address || {};

        this.form.patchValue({
          fullName: user.name || '',
          userName: user.userName || '', // Code added by Sanchit on 09/06/2026
          streetAddress: (address as any).address1 || '',
          city: (address as any).city || '',
          state: (address as any).state || '',
          zip: (address as any).zip || '',
          phone: user.phoneNumber || '',
          phone2: user.altPhone || '',
          email: user.emailAddress || ''
        });
      },
      error: err => {
        console.error('Error fetching user', err);
      }
    });
  }

  // -----------------------------
  // ERROR HELPER FOR TEMPLATE
  // -----------------------------
  getError(controlName: string): string {
    const control = this.form.get(controlName);
    
    if (!control || !control.touched || !control.invalid) {
      return '';
    }

    if (control.hasError('pattern')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('required')) {
      switch (controlName) {
        case 'fullName': return 'Please enter a full name';
        case 'userName': return 'Please enter a user name'; // Code added by Sanchit on 09/06/2026
        case 'streetAddress': return 'Please enter a street address';
        case 'email': return 'Please enter an email address';
        default: return 'Required';
      }
    }

    return '';
  }

  // -----------------------------
  // CANCEL RESET FORM
  // -----------------------------
  onCancel() {
    this.form.reset();

    // Reset checkbox selections
    this.otherDetailsOptions = this.otherDetailsOptions.map(opt => ({
      ...opt,
      checked: false
    }));
    this.location.back();
  }


private focusFirstInvalidField(): void {
  setTimeout(() => {
    // Get all invalid form control names
    const invalidControls = Object.keys(this.form.controls).filter(
      key => this.form.get(key)?.invalid
    );

    if (invalidControls.length === 0) return;

    const firstInvalidControl = invalidControls[0];

    // Find the element with matching formControlName attribute
    const invalidEl = this.el.nativeElement.querySelector(
      `[formControlName="${firstInvalidControl}"]`
    );

    if (invalidEl) {
      const input = invalidEl.querySelector('input, textarea, select') || invalidEl;
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 0);
}

  // -----------------------------
  // FINAL SUBMIT HANDLER
  // -----------------------------
  onSubmit() {
    if (this.isSaving || !this.userRecord) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      this.focusFirstInvalidField();
      return;
    }

    const formVals = this.form.value;
    const newName = (formVals.fullName || '').trim();
    const newUserName = (formVals.userName || '').trim(); // Code added by Sanchit on 09/06/2026
    const newStreetAddress = (formVals.streetAddress || '').trim();
    const newEmail = (formVals.email || '').trim();
    const newPhone = (formVals.phone || '').trim();
    const newAltPhone = (formVals.phone2 || '').trim();

    const emailChanged =
      newEmail.toLowerCase() !==
      (this.userRecord.emailAddress || '').trim().toLowerCase();

    const payload: Partial<User> = {
      ...this.userRecord,
      id: this.userId,
      // Never mutate username from profile edit; it can invalidate session/login mapping.
      // userName: this.userRecord.userName || this.context.user?.userName,
      isEnabled:
        typeof this.userRecord.isEnabled === 'boolean'
          ? this.userRecord.isEnabled
          : true,
      name: newName,
      userName: newUserName, // Code added by Sanchit on 09/06/2026
      emailAddress: newEmail,
      phoneNumber: newPhone,
      altPhone: newAltPhone,
      address: {
        ...(this.userRecord.address || {}),
        name: (this.userRecord.address as any)?.name || 'Default',
        parentType: 'User',
        address1: newStreetAddress,
        address2: '',
        city: (formVals.city || '').trim(),
        state: (formVals.state || '').trim(),
        zip: (formVals.zip || '').trim()
      } as Partial<UserAddress>,
      userProfileImageURL: this.context.user?.userProfileImageURL
    };

    // Some backend mappings persist from emailAddresses[] instead of emailAddress.
    const existingEmailAddresses = (this.userRecord as any)?.emailAddresses;
    if (Array.isArray(existingEmailAddresses)) {
      const clonedEmailAddresses = existingEmailAddresses.map((e: any) => ({ ...e }));
      if (clonedEmailAddresses.length > 0) {
        clonedEmailAddresses[0].email = newEmail;
      } else {
        clonedEmailAddresses.push({
          name: 'Default',
          email: newEmail,
          parentType: 'User',
          parent: { id: this.userId }
        });
      }
      (payload as any).emailAddresses = clonedEmailAddresses;
    } else {
      (payload as any).emailAddresses = [
        {
          name: 'Default',
          email: newEmail,
          parentType: 'User',
          parent: { id: this.userId }
        }
      ];
    }

    this.isSaving = true;
    const verifyAndFinish = () => {
      this._securitySvc.getUser(this.userId).subscribe({
        next: savedUser => {
          const savedEmail = (savedUser?.emailAddress || '').trim();
          const savedName = (savedUser?.name || '').trim();
          const savedPhone = (savedUser?.phoneNumber || '').trim();

          const emailSaved = savedEmail.toLowerCase() === newEmail.toLowerCase();
          const nameSaved = savedName === newName;
          const phoneSaved = savedPhone === newPhone;

          if (nameSaved && phoneSaved && (emailSaved || !emailChanged)) {
            this.isSaving = false;
            this.userRecord = savedUser;
            this.context.user = { ...this.context.user, ...savedUser };
            this.context.user$.emit(this.context.user);
            this.toastr.success('Profile saved successfully.');
            
            // Added a 0.5-second delay to allow the toaster to be seen
            setTimeout(() => {
              this.redirectToHome();
            }, 500);
            
            return;
          }

          this.isSaving = false;
          this.userRecord = savedUser;
          this.toastr.error(
            'Email update was not accepted by server mapping. Please use a unique login/email or contact support.',
            'Email not saved',
            { disableTimeOut: true }
          );
        },
        error: err => {
          this.isSaving = false;
          this.toastr.error(
            'Profile save could not be verified. Please refresh and confirm your changes.',
            'Verification failed',
            { disableTimeOut: true }
          );
          console.error('Verify saved user error:', err);
        }
      });
    };

    this._securitySvc.saveUser(payload).subscribe({
      next: res => {
        if (res?.responseMessage) {
          this.isSaving = false;
          this.toastr.error(res.responseMessage, 'Could not save profile', {
            disableTimeOut: true
          });
          return;
        }
        verifyAndFinish();
      },
      error: err => {
        this.isSaving = false;
        this.toastr.error('Could not save profile. Please try again.', 'Save failed', {
          disableTimeOut: true
        });
        console.error('Save user error:', err);
      }
    });
  }

  // -----------------------------
  // BACK HANDLER
  // -----------------------------
  onBack() {
    this.location.back();
  }

  private redirectToHome(): void {
    const from = this.route.snapshot.queryParamMap.get('from');

    if (from === 'staff') {
      this.location.back();
      return;
    }

    const clientIdFromContext = this.context?.client?.id;
    const clientIdFromUrl =
      this.router.url.match(/\/jurisdiction\/([^\/]+)/)?.[1] || null;
    const clientId = clientIdFromContext || clientIdFromUrl;

    if (clientId) {
      this.router.navigateByUrl(
        `/jurisdiction/${clientId}/${applicantHomeRoutesNames.root}/${applicantHomeRoutesNames.applyForPermit.route}`
      );
      return;
    }

    this.router.navigateByUrl(
      `/${applicantHomeRoutesNames.root}/${applicantHomeRoutesNames.applyForPermit.route}`
    );
  }

  toggleCheckbox(index: number) {
    this.otherDetailsOptions[index].checked = !this.otherDetailsOptions[index]
      .checked;
  }
}
